import {
    InterfaceFactoryOptions,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import {
    getControlValues,
    getPathList,
    getSize,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { IElementBaseComponent } from './element-base-component.interface';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import {
    generateGraphicsModel, isSpecialPath,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import type { IPath } from '@tencent/alloy-graphics/lib/types/path';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {
    BaseComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component';
import { rgbaToHex } from '@src/slide/infrastructure/module/independent/color/transform';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';

export class ElementBaseComponent extends BaseComponent implements IElementBaseComponent {
    // 各图形公用的graphics部分
    private baseGraphics: GraphicsRenderComponent;
    // 缓存 alloy-graphics 组件 options
    private graphicsOptions: ReturnType<typeof generateGraphicsModel>;

    public constructor(public factoryOptions: InterfaceFactoryOptions<IElementBaseModel>) {
        super();
    }

    public getUniqueId(): string {
        return this.factoryOptions.model.key;
    }

    public getComponentType(): string {
        return this.factoryOptions.model.type;
    }

    /**
     * 根据style生成graphics构造所需的model
     */
    public generateGraphicsModel = () => {
        const elementModel = this.factoryOptions.model;
        const frameOnSlide = elementModel.getFrame?.().frameOnSlide();
        const realRotateAndFlip = {
            rotate: frameOnSlide.rotate,
            flipH: frameOnSlide.flipH,
            flipV: frameOnSlide.flipV,
        };
        const size = getSize(this.factoryOptions.frame, this.factoryOptions.model.props);
        const controlValues = getControlValues(this.factoryOptions.model, this.factoryOptions.model.props);
        const style = {
            ...this.factoryOptions.model.props.style,
            ...size, // 拖拽调整宽高，需要用实时宽高覆盖掉style中的
            controlValues, // 拖拽调整controlValues，需要用实时controlValues覆盖掉style中的
            pathList: getPathList(this.factoryOptions.model),
            fillBackground: this.getFillBackground(),
            border: this.getBorder(),
        };
        const { bgShape } = elementModel.props.style;
        if (elementModel.parent?.type !== UIModelType.GROUP && isSpecialPath(bgShape)) {
            // 特殊图形有部分区域在选中框外，导致选中框外渐变填充和图片填充不显示，使用 offsetFrame 根据形状区域扩展填充区域
            const wrapRect = this.factoryOptions.model.pathParser.getShapeWrapRect();
            style.offsetFrame = {
                offsetX: wrapRect.left,
                offsetY: wrapRect.top,
                offsetWidth: wrapRect.width,
                offsetHeight: wrapRect.height,
                offsetFlipH: !!(realRotateAndFlip.flipH),
                offsetFlipV: !!(realRotateAndFlip.flipV),
                offsetRotate: 0,
            };
        }
        return generateGraphicsModel({
            style,
            factoryOptions: this.factoryOptions,
            isOutsizeBorder: this.factoryOptions.isOutSideBorder,
            realRotateAndFlip,
            applyImageFilter: this.factoryOptions.model.type !== UIModelType.IMAGE,
        });
    };

    /**
     * 生成graphicsModel,然后生成canvas graphics
     */
    public renderComponent = () => {
        const graphicsModel = this.generateGraphicsModel();
        this.graphicsOptions = graphicsModel;
        if (!this.baseGraphics) {
            this.baseGraphics = new GraphicsRenderComponent(graphicsModel);
            this.addChild(this.baseGraphics);
        } else {
            this.baseGraphics.updateModel(graphicsModel);
        }
        this.factoryOptions.model.pathParser = this.baseGraphics.parser;
    };

    public async onImageLoaded(callback: () => void): Promise<void> {
        if (this.isImage(this.factoryOptions.model)) {
            await this.baseGraphics.promiseImageLoaded();
            callback();
        }
    }

    public get pathArray(): IPath[] {
        return (this.baseGraphics as any).pathArray;
    }

    private getFillBackground() {
        const { model: elementModel } = this.factoryOptions;
        const anim = this.factoryOptions?.anim;
        const eleFillBackground = elementModel.props.style?.fillBackground;
        if (anim?.fillColor) {
            return {
                solidFill: new SolidFill(rgbaToHex(anim.fillColor)),
            };
        }
        return eleFillBackground;
    }

    private getBorder() {
        const border = this.factoryOptions.model.props.style?.border;
        const anim = this.factoryOptions?.anim;
        if (!anim?.lineColor) return border;

        return {
            ...border,
            color: rgbaToHex(anim.lineColor),
            tdColor: new TDColor({ rgbaStr: anim.lineColor }),
        };
    }

    private isImage(model: IBaseModel): boolean {
        return model.type === UIModelType.IMAGE || !!(model.props.style as any)?.fillBackground?.image?.src;
    }
}
